import router from "../../router";
import customer_routes from "../../routes";
import customer_nav from "../../nav";
import {accountAllowedRoute, accountFilteredMenu} from "../../../js/services/navFilter";
import axios from "axios";
import i18n from '../../../js/services/locale'
import mainOidc from "../../auth";

export default {
    namespaced: true,
    state: {
        token: localStorage.getItem('customer_token') || '',
        status: null,
        settings: {},
        user: {},
        pending_documents: [],
        language: null,
        group: null,
        oidc: {
            role: []
        },
    },
    mutations: {
        logout(state) {
            state.status = null;
            state.token = null;
        },
        auth_success(state, user){
            state.status = 'success';
            state.token = user.token;
            state.user = user;
        },
        auth_request(state) {
            state.status = 'loading';
        },
        auth_error(state) {
            state.status = 'error';
        },
        profile(state, user) {
            state.user = {
                ...state.user,
                user
            };
        },
        settings(state, settings) {
          state.user.customer.settings = settings;
        },
        timezone(state, timezone) {
          state.user.timezone = timezone;
        },
        set_pending_documents(state, data) {
          state.pending_documents = data;
        },
        set_language(state, data) {
          state.language = data;
        },
        set_group(state, data) {
          state.group = data;
        },
        set_oidc(state, data) {
            state.oidc = data;
        },
    },
    getters: {
        isLoggedIn: state => !!state.token,
        authStatus: state => state.status,
        user: state => state.user,
        group: state => state.group,
        dealer: state => state.user.dealer,
        customer: state => state.user.customer,
        language: state => state.language,
        timezone: state => state.user.timezone,
        oidc: state => state.oidc,
        getSetting: (state) => (key, default_value) => state.user.customer?.settings[key] === undefined
            ? default_value
            : state.user.customer?.settings[key]
    },
    actions: {
        logout({commit}){
            return new Promise(async (resolve, reject) => {
                commit('logout');
                localStorage.removeItem('customer_token');
                delete axios.defaults.headers.common['Authorization'];
                await mainOidc.signOut()
                resolve();
            })
        },
        login({commit, dispatch}, user){
            let self = this;
            return new Promise((resolve, reject) => {
                commit('auth_request');

                // Set default TOKEN header
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + mainOidc.accessToken;

                const includes = 'customer,dealer,language,timezone,auth_manager';
                const url = laravel.config.app_url + '/customer/api/v3/profile?include=' + includes;

                axios.get(url, { errorHandle: false })
                    .then(response => {
                        user = {...user, ...response.data.data, ...response.data.data.auth_manager};
                      const languageCode = user?.auth_manager?.locale || user?.language?.code || 'it';

                        if (localStorage.getItem('language') !== languageCode) {
                            window.location = window.location = `/${languageCode}/customer/dashboard`;
                        }
                        commit('set_oidc', mainOidc.userProfile);
                        commit('set_group', 'customer');
                        commit('auth_success', {
                            ...user,
                            group: 'customer',
                            api_path: '/customer/api',
                            token: mainOidc.accessToken,
                        });

                        commit('organization/setActiveOrganization', user.customer?.organization_id ?? "no_organization_id", {root: true});

                        // set acl roles data
                        let roles = accountGetCustomerRoles(user, dispatch);

                        router.addRoutes(customer_routes(languageCode));
                        // acls on routes
                        router.beforeEach((to, from, next) => {
                            if (!user.customer && to.name !== "Registration") {
                                if (from.name === "Registration") {
                                    return false;
                                }
                                return next({ name: "Registration" });
                            }

                            if(user.customer && to.name === "Registration") {
                              return next({name: "Dashboard"});
                            }

                            const allowed = accountAllowedRoute(to, roles);
                            const licensed = licenseAllowedRoute(to, user?.customer?.license?.license_meta?.code ?? "no_licence_code");

                            if (allowed && licensed) {
                                return next();
                            }

                            next({ name: "Home" });
                        });

                        self.commit('setAcls', roles);

                        self.commit('setMenu', accountFilteredMenu(customer_nav.items,roles));
                        self.commit('setSideMenu', accountFilteredMenu(customer_nav.side_items,roles));
                        resolve(user);
                    })
                    .catch(err => {
                        dispatch('logout');
                        localStorage.removeItem('customer_token');
                        reject(err);
                    });
            })
        },
        updateProfile({commit}, user) {
            commit('profile', user);
        },
        updateSettings({commit}, settings) {
            commit('settings', settings);
        },
        updateTimezone({commit}, timezone){
            commit('timezone', timezone);
        },
        updateLanguage({commit}, language_code){
            commit('set_language', {
                code: language_code,
            });

            i18n.locale = language_code;
            axios.get('/lang/' + language_code + '.json')
                .then((response) => {
                    i18n.mergeLocaleMessage(language_code, response.data || {});
                })
        },
        fetchPendingDocuments({commit}){
            const url = '/customer/api/v2/pending-documents-signatures';
            return axios.get(url).then((response) => {
                if (response.data.data.length > 0) commit('set_pending_documents', response.data.data);
                return response.data.data
            });
        },
    },

};

export function accountGetCustomerRoles(user, dispatch) {
    let roles = [];

    if(!user.customer) {
      return roles;
    }

    // Check for markets roles
    if (user.customer.market)
        roles.push(user.customer.market.code);
    else
        roles.push("mkt_unknown");

    // E-Invoice role
    if (user.customer.e_invoice_status) {
        roles.push("e_invoice");
        dispatch('services/getEInvoiceServiceData', null, {root:true});
    }

  // with guests even if abroad customer
  if (user.customer.is_guests_allowed) {
    roles.push('with_guests');
  }


    if(!user.customer?.license) {
      return roles;
    }

    // enterprise role
    if(user?.customer?.license?.license_meta?.code === 'enterprise') {
        roles.push("enterprise_license");
    }

    // Add provisioning feature if provided by the license
    const provisioning_markets = ['mkt_it', 'mkt_de']
    if (user.customer.license?.original_license?.features?.provisioning
        && provisioning_markets.includes(user.customer.market?.code)) {
        roles.push("feat_provisioning");
    }

    return roles;
}

export function licenseAllowedRoute(to,license_code) {
    if (!to.matched) return false; // should never enter

    let allowed = true;
    for (const route of to.matched.slice().reverse()) {
        // check if route is hidden for some roles (hidden is a priority)
        if (route.meta.disabledForLicenseCode && route.meta.disabledForLicenseCode.length) {
            let intersection = route.meta.disabledForLicenseCode.filter((code) => code === license_code);
            if (intersection.length > 0) {
                allowed = false;
                return;
            }
        }
    }
    return allowed;
}
